import React, { useEffect, useState } from "react";
import { ArrowLeft, Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Utils from "../../utils";
import {
  createRewardPrograms,
  detailsRewardPrograms,
  updateRewardPrograms,
} from "../../services/Admin/rewardProgramsService";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import { getAllConfigsList } from "../../services/Admin/commonService";
import Select from "react-select";

const AddRewardPrograms = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    program_type: "",
    avg_cpp: "",
    is_active: 1,
    uuid: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    program_type: "",
    avg_cpp: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [configDataList, setConfigDataList] = useState([]);
  const [rewardDetails, setRewardDetails] = useState("");

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value.trim()) return "Name is required";
        return "";

      case "program_type":
        if (!value.trim()) return "Program type is required";
        if (/\s/.test(value)) return "Program type cannot contain spaces";
        return "";

      case "avg_cpp":
        if (!value) return "Average CPP is required";
        if (isNaN(value)) return "Average CPP must be a number";
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(() => {
    getAllConfigsList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const formatted = decrypted.data.program_type?.map((item) => ({
          value: item,
          label: item,
        }));

        setConfigDataList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (uuid) {
      let data = {
        uuid: uuid,
      };
      detailsRewardPrograms(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setRewardDetails(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [uuid]);

  useEffect(() => {
    if (rewardDetails) {
      setFormData({
        name: rewardDetails?.name,
        program_type: rewardDetails?.program_type,
        avg_cpp: rewardDetails?.avg_cpp,
        is_active: rewardDetails?.is_active,
        uuid: rewardDetails?.uuid,
      });
    }
  }, [rewardDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", formData.name),
      program_type: validateField("program_type", formData.program_type),
      avg_cpp: validateField("avg_cpp", formData.avg_cpp),
    };

    setErrors(newErrors);

    Utils.focusError();

    const hasErrors = Object.values(newErrors).some((err) => err);

    if (hasErrors) return;

    try {
      setBtnLoading(true);
      const payload = {
        name: formData.name?.trim(),
        program_type: formData.program_type,
        avg_cpp: formData.avg_cpp,
        is_active: formData.is_active,

        ...(formData?.uuid && {
          uuid: formData.uuid,
        }),
      };

      const apiCall = formData?.uuid
        ? updateRewardPrograms
        : createRewardPrograms;

      const response = await apiCall(payload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setFormData({
        name: "",
        program_type: "",
        avg_cpp: "",
        is_active: 1,
        uuid: "",
      });
      setErrors({
        name: "",
        program_type: "",
        avg_cpp: "",
      });
      navigate("/reward-programs");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <div className="pb-24 md:pb-0 transition-colors duration-300">
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm p-5 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/reward-programs")}
            className="p-2 -ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl md:text-2xl font-black text-slate-800 dark:text-white tracking-tight">
            {uuid ? "Update Reward Program" : "Add Reward Program"}
          </h2>
        </div>

        <div className="space-y-6 md:space-y-8">
          <div>
            <h3 className="text-[10px] md:text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-5 border-b border-slate-50 dark:border-slate-700/50 pb-2">
              Program Information
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label htmlFor="name" className="input-label">
                    Program Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="input-field"
                    placeholder="Enter program name"
                    value={formData?.name || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.name && (
                    <span className="text-danger">{errors.name}</span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="avg_cpp" className="input-label">
                    Average CPP{" "}
                    <span className="hidden md:inline text-[8px] opacity-70">
                      (cents per point)
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    inputMode="numeric"
                    className="input-field"
                    name="avg_cpp"
                    placeholder="e.g. 2.05"
                    value={formData?.avg_cpp || ""}
                    onChange={handleChange}
                  />
                  {errors.avg_cpp && (
                    <span className="text-danger">{errors.avg_cpp}</span>
                  )}
                </div>
              </div>

              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label htmlFor="category_id" className="input-label">
                    Program Type <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={configDataList}
                    value={getSelectedOption(
                      configDataList,
                      formData.program_type,
                    )}
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        program_type: String(selected?.value ?? ""),
                      }));
                    }}
                    placeholder="Select program type"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                </div>

                {/* <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase">
                    Logo
                  </label>
                  <div className="w-full h-[104px] border-2 border-dashed border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/30 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-orange-200 dark:hover:border-orange-900 hover:bg-orange-50/30 dark:hover:bg-orange-900/10 transition-all group">
                    <Upload
                      size={20}
                      className="text-slate-300 dark:text-slate-600 mb-1 group-hover:text-orange-400 transition-colors"
                    />
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500">
                      Click to upload
                    </p>
                  </div>
                </div> */}
              </div>

              <div className="lg:col-span-2 space-y-3">
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase">
                  Status <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {["Active", "Inactive"].map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-2 cursor-pointer group"
                    >
                      <div className="relative flex items-center justify-center">
                        <input
                          type="radio"
                          name="status"
                          className="peer shrink-0 appearance-none w-4 h-4 border border-slate-300 dark:border-slate-600 rounded-full bg-white dark:bg-slate-900 checked:border-orange-500 dark:checked:border-orange-600 transition-all"
                          defaultChecked={status === "Active"}
                        />
                        <div className="absolute w-2 h-2 rounded-full bg-orange-500 opacity-0 peer-checked:opacity-100 transition-opacity" />
                      </div>
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors">
                        {status}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-4 pt-6 mt-6 border-t border-slate-50 dark:border-slate-700/50">
            <button
              type="button"
              onClick={() => navigate("/reward-programs")}
              className="flex-1 md:flex-none px-6 py-3 rounded-xl font-black text-xs text-slate-400 dark:text-slate-500"
            >
              Cancel
            </button>
            <button
              type="button"
              form="reward-form"
              className="flex-[2] md:flex-none px-8 py-3 bg-[#D4AF37] text-white rounded-xl font-black text-xs shadow-lg shadow-orange-200/50"
              onClick={handleSubmit}
              disabled={btnLoading}
            >
              {btnLoading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {btnLoading
                ? "submitting..."
                : formData.uuid
                  ? "Update"
                  : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddRewardPrograms;
